import { extractStructuredSourceCitation } from '../sourceCitation';
import type { RetrievalSource, SourceTarget } from '../types';

// ─── UUID helpers ─────────────────────────────────────────────────────────────

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const CHUNK_DOC_RE = /_doc_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})_/i;

/**
 * Extracts a document UUID from a string.
 * Handles:
 *   - "_doc_{uuid}_" patterns (chunk labels, chunk_id strings)
 *   - bare UUID strings
 */
export function parseDocIdFromString(s: string | undefined | null): string | null {
  if (!s) return null;
  const str = String(s).trim();
  const chunkMatch = str.match(CHUNK_DOC_RE);
  if (chunkMatch) return chunkMatch[1];
  if (UUID_RE.test(str) && str.length <= 36) return str;
  return null;
}

// ─── Interview detection ──────────────────────────────────────────────────────

/**
 * Detects whether a RetrievalSource refers to an interview document.
 * Priority: source_type → document_type → document_name prefix.
 * Matches the same logic used in the inline document viewers.
 */
export function isInterviewSource(source: RetrievalSource): boolean {
  const srcType = (source.metadata?.source_type as string | undefined) ?? '';
  if (srcType === 'interview_report' || srcType === 'interview_source') return true;
  const docType = ((source.metadata?.document_type as string | undefined) ?? '').toLowerCase();
  if (docType === 'interviewreport' || docType === 'interviewsource') return true;
  const docName = ((source.metadata?.document_name as string | undefined) ?? '').toLowerCase();
  return docName.startsWith('interview-report');
}

function resolveInterviewSourceType(source: RetrievalSource): 'interview_report' | 'interview_source' | null {
  const srcType = (source.metadata?.source_type as string | undefined) ?? '';
  if (srcType === 'interview_report' || srcType === 'interview_source') {
    return srcType;
  }

  const docType = ((source.metadata?.document_type as string | undefined) ?? '').toLowerCase();
  if (docType === 'interviewreport') {
    return 'interview_report';
  }

  if (docType === 'interviewsource') {
    return 'interview_source';
  }

  const docName = ((source.metadata?.document_name as string | undefined) ?? '').toLowerCase();
  if (docName.startsWith('interview-report')) {
    return 'interview_report';
  }

  return null;
}

// ─── Page number extraction ───────────────────────────────────────────────────

function extractPageNumber(source: RetrievalSource): number | null {
  const metadata = source.metadata;
  const citation = extractStructuredSourceCitation(source);
  if (citation.pageNumbers.length > 0) {
    return citation.pageNumbers[0] ?? null;
  }

  const candidates = [
    metadata?.page_numbers,
    metadata?.source_page_numbers,
    metadata?.page_number,
    metadata?.pagenumber,
  ];

  for (const pages of candidates) {
    if (pages == null) {
      continue;
    }

    if (Array.isArray(pages) && pages.length > 0) {
      const page = Number(pages[0]);
      if (Number.isFinite(page) && page > 0) {
        return page;
      }
    }

    if (typeof pages === 'number' || typeof pages === 'string') {
      const page = Number(pages);
      if (Number.isFinite(page) && page > 0) {
        return page;
      }
    }
  }

  return null;
}

// ─── Main resolver ────────────────────────────────────────────────────────────

/**
 * Resolves a RetrievalSource to a SourceTarget for the inline viewer.
 *
 * Fallback chain for documentId:
 *   1. _doc_{uuid}_ in source.name (chunk label)
 *   2. metadata.document_id
 *   3. metadata.source_document_id (Neo4j entity field)
 *   4. UUID extracted from metadata.chunk_id string
 *   5. UUID extracted from metadata.chunk_id_label string
 *   6. UUID extracted from source.name
 *   7. UUID extracted from source.source_ref
 *
 * Returns null if no documentId can be resolved.
 */
export function resolveSourceTarget(source: RetrievalSource): SourceTarget | null {
  const meta = source.metadata;
  const citation = extractStructuredSourceCitation(source);

  const documentId =
    parseDocIdFromString(source.name) ??
    (meta?.document_id as string | undefined) ??
    (meta?.source_document_id as string | undefined) ??
    parseDocIdFromString(meta?.chunk_id as string | undefined) ??
    parseDocIdFromString(meta?.chunk_id_label as string | undefined) ??
    parseDocIdFromString(source.source_ref);

  if (!documentId) return null;

  const interviewSourceType = resolveInterviewSourceType(source);
  const pageNumber = extractPageNumber(source);
  const sourceType = interviewSourceType ?? ((meta?.source_type as string | undefined) ?? source.type ?? 'document');

  const candidateNames = [
    citation.documentName,
    (meta?.source_document_name as string | undefined) ?? undefined,
    (meta?.document_name as string | undefined) ?? undefined,
    source.source_ref && !source.source_ref.includes('_doc_') ? source.source_ref : undefined,
    source.name && !source.name.includes('_doc_') ? source.name : undefined,
  ].filter((value): value is string => Boolean(value && value.trim()));

  const fileName = candidateNames[0] ?? `Dokument ${documentId.slice(0, 8)}…`;

  return { documentId, pageNumber, sourceType, fileName, highlight: citation.highlight };
}
